import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, Copy } from "lucide-react";
import { copyText } from "@/lib/clipboard";

const CodeBlock = ({ inline, className, children }) => {
  const [copied, setCopied] = useState(false);
  const text = String(children ?? "");
  if (inline) {
    return <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.85em] font-mono text-[#B4451F]">{children}</code>;
  }
  const copy = async () => {
    const ok = await copyText(text.replace(/\n$/, ""));
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };
  return (
    <div className="group relative my-4">
      <button onClick={copy} data-testid="copy-code-btn"
        className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-md border border-slate-600/40 bg-slate-800/80 px-2 py-1 text-[11px] font-medium text-slate-100 opacity-0 transition-opacity group-hover:opacity-100">
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className="overflow-x-auto rounded-xl bg-[#0B0E14] p-4 text-[13px] leading-relaxed text-slate-100">
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
};

/** ChatGPT-style flowing Markdown — headings, lists, tables, code, links. */
export function Markdown({ children }) {
  return (
    <div className="ai-prose text-[15px] leading-[1.7] text-slate-700" data-testid="ai-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: (p) => <h1 className="mb-3 mt-5 text-xl font-bold tracking-tight text-slate-900" {...p} />,
          h2: (p) => <h2 className="mb-2.5 mt-5 text-lg font-bold tracking-tight text-slate-900" {...p} />,
          h3: (p) => <h3 className="mb-2 mt-4 text-base font-semibold text-slate-900" {...p} />,
          p: (p) => <p className="mb-4 last:mb-0" {...p} />,
          ul: (p) => <ul className="mb-4 list-disc space-y-1.5 pl-5 marker:text-slate-400" {...p} />,
          ol: (p) => <ol className="mb-4 list-decimal space-y-1.5 pl-5 marker:text-slate-400" {...p} />,
          li: (p) => <li className="pl-1" {...p} />,
          a: (p) => <a className="font-medium text-[#B4451F] underline underline-offset-2" target="_blank" rel="noreferrer" {...p} />,
          strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
          blockquote: (p) => <blockquote className="my-4 border-l-2 border-[#EA580C]/50 pl-4 italic text-slate-500" {...p} />,
          hr: () => <hr className="my-5 border-slate-200" />,
          table: (p) => (
            <div className="my-4 overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full border-collapse text-sm" {...p} />
            </div>
          ),
          thead: (p) => <thead className="bg-slate-50" {...p} />,
          th: (p) => <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700" {...p} />,
          td: (p) => <td className="border-b border-slate-100 px-3 py-2 text-slate-600" {...p} />,
          code: CodeBlock,
        }}>
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
